/**
 * English person titles from the client master data (cl_id title list, received 2026-07).
 *
 * Hand-maintained — edit directly when the client delivers a new list. Derivation
 * from the master data's `abbr_en` column, falling back to `name_en` when
 * `abbr_en` is blank, aligned to each entry of {@link THAI_TITLES} (see
 * ./thai-titles.ts) in order, deduplicated, with Mr./Mrs./Miss/Master/Ms.
 * pinned first (Ms. has no Thai counterpart in the master data).
 *
 * Casing/punctuation is kept verbatim from the client sheet — it is
 * inconsistent (e.g. "GEN." vs "Act.Flt.Lt.") but is the source of truth.
 *
 * Hand-fixes applied where the sheet had no value or a data-entry error, not
 * yet confirmed by the client — flag for review on the next data refresh:
 *   ด.ช. / ด.ญ.                    -> Master / Miss   (no master-data value; existing app titles)
 *   ม.จ.                          -> M.C.             (no value; taken from the ม.จ. หญิง row)
 *   พระวรวงศ์เธอพระองค์เจ้า        -> HRH.             (no value; taken from its หญิง row)
 *   ส.ต.ต.                        -> Pol.L/Cpl.       (sheet had Thai text "POL.L หรือ C"; ส.ต.ต. หญิง row says Pol.L/Cpl.)
 *   ว่าที่ ร.ต. หญิง               -> Acting 2nd Lt.   (sheet had a stray comma: "Acting 2, LT.")
 *
 * Keep in sync with set-api-monorepo libs/new-api-core/src/shared/english-titles.ts.
 */

/**
 * Canonical English person titles, deduplicated, in display order.
 */
// cspell:disable
export const ENGLISH_TITLES = [
  'Mr.',
  'Mrs.',
  'Miss',
  'Master',
  'Ms.',
  'Field Marshal',
  'GEN.',
  'LT.GEN.',
  'MAJ.GEN.',
  'BRIG.GEN.',
  'COL.',
  'LT.COL.',
  'MAJ.',
  'CAPT.',
  'LT.',
  'SUBLT.',
  'ACTING SUBLT.',
  'SM1',
  'SM2',
  'SM3',
  'SGT.',
  'CPL.',
  'LCPL.',
  'ADM.',
  'VADM.',
  'RADM.',
  'CDRE.',
  'CDR.',
  'LCDR.',
  'CPO1',
  'CPO2',
  'CPO3',
  'PO1',
  'PO2',
  'PO3',
  'ACM',
  'AM.',
  'AVM.',
  'ACDR.',
  'FS1',
  'FS2',
  'FS3',
  'POL.GEN.',
  'POL.LT.GEN.',
  'POL.MAJ.GEN.',
  'POL.BRIG.GEN.',
  'POL.COL.',
  'POL.LT.COL.',
  'POL.MAJ.',
  'POL.CAPT.',
  'POL.LT.',
  'POL.SUB.LT.',
  'POL.SEN.SGT.MAJ.',
  'POL.SGT.',
  'POL.CPL.',
  'Pol.L/Cpl.',
  'M.R.',
  'M.L.',
  'Khunying',
  'Thanphuying',
  'M.C.',
  'HRH.',
  'Acting Lt.',
  'Acting 2nd Lt.',
  'Col.',
  'Tdvc.Col.',
  'Tdvc.LT.Col.',
  'Acting Capt.',
  'Acting Maj.',
  'Acting Pol.Sub.Lt',
  'MOM',
  'RAdm.',
  'Sgt.',
  'Cdr.',
  'Buddhist Monk',
  'VDC Capt.',
  'Sgt. Maj.',
  'Pol.Col.',
  'K.',
  'T.P.M.L.',
  'Pr.',
  'Prn.',
  'HM.',
  'Gen.',
  'Lt. Gen.',
  'Lt. Col.',
  'Maj.',
  'Act. Maj.',
  'Act. Capt.',
  'Lt.',
  'Act. Lt.',
  'Sub Lt.',
  'Cpl.',
  'Pfc.',
  'Capt.M.C.',
  'Lt.Gen.M.C.',
  'SubLt.M.C.',
  'Lt.M.C.',
  'Lt.Col.M.C.',
  'Col.M.C.',
  'Maj.M.R.',
  'Lt.Col.M.R.',
  'Pfc.M.R.',
  'Col.M.R.',
  'SM1.M.R.',
  'Capt.M.R.',
  'SubLt.M.R.',
  'Sgt.M.R.',
  'Lt.M.R.',
  'Capt.M.L.',
  'Cpl.M.L.',
  'Lt.Gen.M.L.',
  'Lt.M.L.',
  'SubLt.M.L.',
  'Sgt.M.L.',
  'Maj.Gen.M.L.',
  'Maj.M.L.',
  'Col.M.L.',
  'Lt.Col.M.L.',
  'SM3.M.L.',
  'Act.SubLt.M.L.',
  'SM1.M.L.',
  'Lt.Col.L.',
  'Maj. H.H.',
  'SM3.M.R.',
  'Maj.Gen.M.R.',
  'Maj. H.R.H.',
  'T.P. M.R.',
  'Gen.M.L.',
  'Act.SubLt.M.R.',
  'Col. T.P.',
  'Gen. T.P.',
  'Lt.Gen.M.R.',
  'Maj. K.Y.',
  'Capt. K.Y.',
  'Adm.',
  'VAdm.',
  'Capt.',
  'LCdr.',
  'Act.LCdr.',
  'Cdre.',
  'Cdr. M.C.',
  'Capt. M.C.',
  'Lt. Cdr. M.C.',
  'RADM M.R.',
  'Capt. M.R.',
  'Cdr. M.R.',
  'Lt. Cdr. M.R.',
  'Cdr. M.L.',
  'Lt. Cdr. M.L.',
  'CPO1 M.L.',
  'RADM M.C.',
  'Capt. M.L.',
  'RAdm. M.L.',
  'Cdr. K.Y.',
  'AM',
  'AVM',
  'AVM M.R.',
  'AM M.L.',
  'FS1 M.L.',
  'ACM M.R.',
  'AVM M.L.',
  'AM M.R.',
  'Gp.Capt M.L.',
  'ACM M.L.',
  'Pol. Gen.',
  'Act.Pol.Gen.',
  'Pol.Lt.Gen.',
  'Act.Pol.Lt.Gen.',
  'Pol.Maj.Gen.',
  'Act.Pol.Maj.Gen.',
  'Pol.Brig.Gen.',
  'Act.Pol.Brig.Gen.',
  'Act.Pol.Col.',
  'Pol.Lt.Col.',
  'Act.Pol.Lt.Col.',
  'Pol.Maj.',
  'Act.Pol.Maj.',
  'Pol.Capt.',
  'Act.Pol.Capt.',
  'Pol.Lt.',
  'Act.Pol.Lt.',
  'Pol.Sub-Lt.',
  'Act.Pol.Sub-Lt.',
  'Pol.Snr.Sgt.Maj.',
  'Pol.Sgt.',
  'Pol.Cpl.',
  'VDC. Field Marshal',
  'VDC Gen.',
  'VDC Lt.Gen.',
  'VDC Maj.Gen.',
  'VDC Capt',
  'VDC 1st Lt',
  'VDC 2nd Lt',
  'VDC SM(S)',
  'VDC SM1',
  'VDC SM2',
  'VDC SM3',
  'Pol. Lt. Col. M.C.',
  'Pol. Col. M.C.',
  'Pol. Maj. Gen. M.R.',
  'Pol. Maj. M.R.',
  'Pol. Lt. Col. M.R.',
  'Pol. Col. M.R.',
  'Pol. Capt. M.R.',
  'Pol. Sgt. M.L.',
  'Pol. Col. M.L.',
  'Pol. Lt. Col. M.L.',
  'Pol. Lt. M.L.',
  'Pol.Snr.Sgt.Maj.M.L.',
  'Pol. Maj. M.L.',
  'Prof. Dr. Pol. Col.',
  'Pol. Capt. M.L.',
  'Pol. Col. T.P.',
  'Pol. Maj. Gen. M.L.',
  'Maj. Gen. K.Y.',
  'Pol. Lt. Gen. M.L.',
  'Pol. Sub Lt. M.L.',
  'AVM K.Y.',
  'Phra Khru Palad',
  'Phra Samuh',
  'Phra Khru Samu',
  'Phra Khru',
  'Phrakhru Baidika',
  'Phra Khru Dhammadhorn',
  'Phra Kru Sangkharak',
  'Novice',
  'Phra Palad',
  'Phra Baidika',
  'Sqn. Ldr. M.R.',
] as const;
// cspell:enable
